import React, { useState } from 'react'
import {
  Box,
  Grid,
  generateUtilityClasses,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TableSortLabel,
  Tooltip,
  MenuItem,
  TablePagination,
} from '@mui/material'
import { CircularProgress } from '@mui/material'
import { UspDealerViewResult } from '../../../../../../models'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { styled } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import { appRoute } from '../../../../../../constants'
import { storageGet, storageSet } from '../../../../../../utils/storage'
import { stableSort, getComparator } from '../../../../../../utils/sorting'
import { visuallyHidden } from '@mui/utils'
import { GridActionsCellItem } from '@mui/x-data-grid'
import Iconify from '../../../../../../components/iconify'
import { usePopover } from '../../../../../../components/custom-popover'
import CustomPopover from '../../../../../../components/custom-popover/custom-popover'
import Label from '../../../../../../components/label/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'inactiveItem', 'loading', 'tableHead', 'tableCellName'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  searchDealerData?: UspDealerViewResult[]
  loading: boolean
  deleteDealer: (arg: { dealerId: string }) => void
}

const DealersTableBlock = ({ searchDealerData, loading, deleteDealer }: Props) => {
  const navigate = useNavigate()
  const initialOrder = (storageGet('dealers_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('dealers_order_by') || 'Name') as keyof UspDealerViewResult
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof UspDealerViewResult>(initialOrderBy)
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)
  const paginatedData = searchDealerData

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleNavigate = (dealerId: string): void => {
    navigate(appRoute.DealerAddEdit.replace(':dealerId', dealerId))
  }

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspDealerViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('dealers_order', isAsc ? 'desc' : 'asc')
    storageSet('dealers_order_by', property)
  }

  const createSortHandler = (property: keyof UspDealerViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'Name' ? order : false}>
                <TableSortLabel active={orderBy === 'Name'} direction={orderBy === 'Name' ? order : 'asc'} onClick={createSortHandler('Name')}>
                  Name
                  {orderBy === 'Name' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'Address' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'Address'}
                  direction={orderBy === 'Address' ? order : 'asc'}
                  onClick={createSortHandler('Address')}
                >
                  Address
                  {orderBy === 'Address' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'PhoneNumber' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'PhoneNumber'}
                  direction={orderBy === 'PhoneNumber' ? order : 'asc'}
                  onClick={createSortHandler('PhoneNumber')}
                >
                  Phone
                  {orderBy === 'PhoneNumber' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'DealerCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'DealerCode'}
                  direction={orderBy === 'DealerCode' ? order : 'asc'}
                  onClick={createSortHandler('DealerCode')}
                >
                  Short Code
                  {orderBy === 'DealerCode' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), paginatedData as any[])
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ?.map((item) => (
                <TableRow key={item?.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell className={classes.tableCellName} onClick={() => handleNavigate(item?.ID?.toString() || '')}>
                    {item.Name}
                  </TableCell>
                  <TableCell>{Array.isArray(item?.Address) && item?.Address?.length > 0 ? item?.Address[0]?.Address1 : ''}</TableCell>
                  <TableCell>{item?.PhoneNumber}</TableCell>
                  <TableCell>{item?.DealerCode}</TableCell>
                  <TableCell>
                    <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                  </TableCell>
                  <TableCell align={'right'}>
                    <IconButton
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        setSelected(item)
                        popover.onOpen(event)
                      }}
                    >
                      <Iconify icon='eva:more-vertical-fill' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {searchDealerData?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
          <MenuItem
            onClick={(e) => {
              handleNavigate(selected?.ID?.toString() as string)
              popover.onClose()
            }}
          >
            <Iconify icon='solar:pen-bold' />
            Edit
          </MenuItem>
          <MenuItem
            disabled={!selected?.IsActive}
            onClick={() => {
              deleteDealer({ dealerId: selected?.ID?.toString() as string })
              popover.onClose()
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon='solar:trash-bin-trash-bold' />
            Delete
          </MenuItem>
        </CustomPopover>
      </TableContainer>

      <Box sx={{ width: '100%', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
        <Paper>
          <TablePagination
            component='div'
            count={searchDealerData?.length as number}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  )
}

export default DealersTableBlock
